import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { getStationBySlug, getRecentStationEpisodes } from '../api/client';
import { HiPlay, HiArrowLeft } from 'react-icons/hi2';
import { useStationSlug } from '../contexts/StationSlugContext';
import type { Stream, RecentEpisode } from '../types';

const EPISODES_PER_PAGE = 20;

export function BrowseEpisodes() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const appSlug = useStationSlug();
  const streamSlug = appSlug || searchParams.get('stream');

  const [stream, setStream] = useState<Stream | null>(null);
  const [episodes, setEpisodes] = useState<RecentEpisode[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(true);

  const loadMoreRef = useRef<HTMLDivElement>(null);

  // Load stream data
  useEffect(() => {
    if (!streamSlug) {
      setError('No station specified');
      setLoading(false);
      return;
    }

    async function loadStream() {
      try {
        const data = await getStationBySlug(streamSlug!);
        setStream(data.stream);

        // Load initial episodes
        const initialEpisodes = await getRecentStationEpisodes(data.stream.streamid, EPISODES_PER_PAGE);
        setEpisodes(initialEpisodes);
        setHasMore(initialEpisodes.length === EPISODES_PER_PAGE);
        setLoading(false);
      } catch {
        setError('Failed to load station');
        setLoading(false);
      }
    }

    loadStream();
  }, [streamSlug]);

  // Load more episodes (API uses limit-based pagination, not offset)
  const loadMore = useCallback(async () => {
    if (!stream || loadingMore || !hasMore) return;

    setLoadingMore(true);
    try {
      const newLimit = episodes.length + EPISODES_PER_PAGE;
      const allEpisodes = await getRecentStationEpisodes(stream.streamid, newLimit);

      // Check if we got more than we had
      if (allEpisodes.length <= episodes.length) {
        setHasMore(false);
      } else {
        setEpisodes(allEpisodes);
        setHasMore(allEpisodes.length === newLimit);
      }
    } catch (err) {
      console.error('Failed to load more episodes:', err);
    } finally {
      setLoadingMore(false);
    }
  }, [stream, episodes.length, loadingMore, hasMore]);

  // Infinite scroll with Intersection Observer
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !loadingMore) {
          loadMore();
        }
      },
      { threshold: 0.1 }
    );

    if (loadMoreRef.current) {
      observer.observe(loadMoreRef.current);
    }

    return () => observer.disconnect();
  }, [loadMore, hasMore, loadingMore]);

  // Update document title
  useEffect(() => {
    if (stream) {
      document.title = `Browse Episodes - ${stream.streamname}`;
    }
    return () => {
      document.title = 'Player';
    };
  }, [stream]);

  const handleNavigateToEpisode = (episodeId: number) => {
    navigate(`/${episodeId}`);
  };

  const handleGoBack = () => {
    if (stream?.rssfilename) {
      navigate(`/?live=true&stream=${stream.rssfilename}`);
    } else {
      navigate('/');
    }
  };

  // Format date for display
  const formatDate = (timestamp: number) => {
    const date = new Date(timestamp * 1000);
    const now = new Date();
    const diffDays = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60 * 24));

    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;

    return date.toLocaleDateString(undefined, {
      month: 'short',
      day: 'numeric',
      year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined,
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[var(--color-bg)]">
        <div className="text-center">
          <div
            className="inline-block w-10 h-10 border-3 border-[var(--color-border)]
                        border-t-[var(--color-primary)] rounded-full animate-spin"
          />
          <p className="mt-3 text-sm text-[var(--color-text-muted)]">Loading episodes...</p>
        </div>
      </div>
    );
  }

  if (error || !stream) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[var(--color-bg)]">
        <div className="text-center p-6">
          <h1 className="text-xl font-semibold text-[var(--color-text)] mb-2">
            Station Not Found
          </h1>
          <p className="text-sm text-[var(--color-text-muted)] mb-4">
            {error || 'This station could not be found.'}
          </p>
          <button
            onClick={() => navigate('/')}
            className="text-sm text-[var(--color-primary)] hover:underline"
          >
            Go back
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[var(--color-bg)]" style={{ minHeight: '100dvh' }}>
      {/* Header */}
      <header
        className="sticky top-0 z-40 bg-[var(--color-surface)]/90 backdrop-blur-xl border-b border-[var(--color-border)]"
        style={{ paddingTop: 'var(--safe-area-inset-top)' }}
      >
        <div className="max-w-4xl mx-auto px-4 py-3 flex items-center gap-4">
          <button
            onClick={handleGoBack}
            className="p-2 -ml-2 rounded-full hover:bg-[var(--color-surface-elevated)] transition-colors"
            aria-label="Go back"
          >
            <HiArrowLeft size={20} className="text-[var(--color-text)]" />
          </button>

          <div className="flex items-center gap-3 flex-1 min-w-0">
            <img
              src={stream.image}
              alt=""
              className="w-10 h-10 rounded-lg object-cover flex-shrink-0"
            />
            <div className="min-w-0">
              <h1 className="text-base font-semibold text-[var(--color-text)] truncate">
                {stream.streamname}
              </h1>
              <p className="text-xs text-[var(--color-text-muted)]">
                {episodes.length} episodes
              </p>
            </div>
          </div>

          {/* Live button */}
          {stream.url && (
            <button
              onClick={() => navigate(`/?live=true&stream=${stream.rssfilename}`)}
              className="px-3 py-1.5 text-xs font-semibold rounded-full bg-red-500 text-white
                        hover:bg-red-600 transition-colors flex items-center gap-1.5"
            >
              <span className="w-1.5 h-1.5 bg-white rounded-full animate-pulse" />
              LIVE
            </button>
          )}
        </div>
      </header>

      {/* Episodes Grid */}
      <main className="max-w-4xl mx-auto px-4 py-6">
        {episodes.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-[var(--color-text-muted)]">No episodes available</p>
          </div>
        ) : (
          <div className="space-y-2">
            {episodes.map((episode) => (
              <button
                key={episode.episodeid}
                onClick={() => handleNavigateToEpisode(episode.episodeid)}
                className="w-full flex items-center gap-3 p-3 rounded-xl
                          bg-[var(--color-surface)]/60 hover:bg-[var(--color-surface)]
                          border border-[var(--color-border)]/30 hover:border-[var(--color-border)]
                          transition-all group text-left"
              >
                {/* Episode Image */}
                <div className="relative w-16 h-16 flex-shrink-0 rounded-lg overflow-hidden bg-[var(--color-border)]">
                  <img
                    src={episode.image || stream.image}
                    alt=""
                    className="w-full h-full object-cover"
                    loading="lazy"
                    onError={(e) => {
                      const target = e.target as HTMLImageElement;
                      if (target.src !== stream.image) {
                        target.src = stream.image;
                      }
                    }}
                  />
                  <div className="absolute inset-0 bg-black/50 flex items-center justify-center
                                opacity-0 group-hover:opacity-100 transition-opacity">
                    <HiPlay size={24} className="text-white" />
                  </div>
                </div>

                {/* Episode Info */}
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-[var(--color-text)] truncate group-hover:text-[var(--color-primary)] transition-colors">
                    {episode.episodetitle}
                  </p>
                  <p className="text-xs text-[var(--color-text-muted)] truncate">
                    {episode.showtitle}
                  </p>
                  <p className="text-[10px] text-[var(--color-text-muted)] mt-1">
                    {formatDate(episode.datetime)}
                  </p>
                </div>
              </button>
            ))}
          </div>
        )}

        {/* Load More Trigger */}
        <div ref={loadMoreRef} className="py-8 flex justify-center">
          {loadingMore && (
            <div className="flex items-center gap-2 text-[var(--color-text-muted)] text-sm">
              <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
              <span>Loading more...</span>
            </div>
          )}
          {!hasMore && episodes.length > 0 && (
            <p className="text-sm text-[var(--color-text-muted)]">
              No more episodes
            </p>
          )}
        </div>
      </main>

      {/* Footer */}
      <footer
        className="border-t border-[var(--color-border)]/30 bg-[var(--color-surface)]/60"
        style={{ paddingBottom: 'var(--safe-area-inset-bottom)' }}
      >
        <div className="max-w-4xl mx-auto px-4 py-3 text-center">
          <span className="text-[10px] text-[var(--color-text-muted)]">
            Content by {stream.streamname}
          </span>
        </div>
      </footer>
    </div>
  );
}
